import { FC, useCallback } from 'react';
import { useFragment, useMutation } from 'react-relay';
import { Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import Stack from '@mui/material/Stack';
import FormControlLabel from '@mui/material/FormControlLabel';
import AccordionController from 'components/AccordionController';
import Section from 'components/Section';
import TextField from 'components/TextField';
import Form from 'components/Form';
import Checkbox from 'components/Checkbox';

import { useErrorHandler } from 'lib';
import { CateringEditForm_cateringOption$key } from '__generated__/CateringEditForm_cateringOption.graphql';
import { CateringEditFormMutation } from '__generated__/CateringEditFormMutation.graphql';

type FormValues = {
  name: string;
  etgKey: string;
  displayInSearch: boolean;
};

type Props = {
  cateringOption: CateringEditForm_cateringOption$key;
};

const CateringEditForm: FC<Props> = ({ cateringOption }) => {
  const errorHandler = useErrorHandler();

  const { id, name, etgKey, displayInSearch } = useFragment(
    graphql`
      fragment CateringEditForm_cateringOption on CateringOption {
        id
        name
        etgKey
        displayInSearch
      }
    `,
    cateringOption
  );

  const [update, loading] = useMutation<CateringEditFormMutation>(graphql`
    mutation CateringEditFormMutation(
      $id: ID!
      $input: UpdateCateringOptionInput!
    ) {
      updateCateringOption(id: $id, input: $input) {
        ...CateringEditForm_cateringOption
      }
    }
  `);

  const initial = {
    name: name ?? '',
    etgKey: etgKey ?? '',
    displayInSearch,
  };

  const handleSubmit = useCallback(
    (input: FormValues) => {
      update({
        variables: { id, input },
        ...errorHandler({
          successMessage: 'Išsaugota',
        }),
      });
    },
    [errorHandler, id, update]
  );

  return (
    <Formik<FormValues> initialValues={initial} onSubmit={handleSubmit}>
      <Form loading={loading}>
        <AccordionController id="cateringEdit">
          <Section id="main" title="Bendra informacija">
            <Stack spacing={4}>
              <TextField autoFocus name="name" label="Pavadinimas" fullWidth />

              <TextField name="etgKey" label="ETG ID" fullWidth />

              <FormControlLabel
                control={<Checkbox name="displayInSearch" />}
                label="Rodyti paieškoje"
              />
            </Stack>
          </Section>
        </AccordionController>
      </Form>
    </Formik>
  );
};

export default CateringEditForm;
